// Boleto virtual de sorteo con el mismo diseño del boleto físico (foto de Tuto,
// 2026-10-30): talón a la izquierda con los datos del comprador, logo B&B,
// marca + "BOLETO SORTEO" + pastilla con el lugar, foto del premio, sello rojo
// con el precio y el número del boleto en rojo. Se exporta a PNG con
// html-to-image (ver downloadTicketPng) y se imprime desde el navegador.
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { toPng } from 'html-to-image';

export const TICKET_W = 1000;
export const TICKET_H = 380;

const NAVY = '#1d2a5c';
const BLUE = '#2b57a3';
const RED = '#d8322a';
const GOLD = '#f3e6bd';
const GRAY = '#cfd6e2';
const FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif";

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmtDateParts(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return { line1: `${months[m - 1]} ${d}`, line2: String(y) };
}

// Sello redondo dentado ("BOLETO $100.00 C/U · solo 500 participantes").
function Seal({ price, total, size = 118 }) {
  const r = size / 2;
  const pts = [];
  const teeth = 28;
  for (let i = 0; i < teeth * 2; i += 1) {
    const a = (Math.PI * i) / teeth;
    const rr = i % 2 === 0 ? r : r * 0.9;
    pts.push(`${(r + rr * Math.cos(a)).toFixed(1)},${(r + rr * Math.sin(a)).toFixed(1)}`);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <polygon points={pts.join(' ')} fill="#ee6a63" opacity="0.92" />
      <circle cx={r} cy={r} r={r * 0.78} fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.7" />
      <text x={r} y={r - 16} textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize="15" fill="#fff">BOLETO</text>
      <text x={r} y={r + 2} textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize="13" fill="#fff">{money(price)} C/U</text>
      <text x={r} y={r + 16} textAnchor="middle" fontFamily={FONT} fontWeight="600" fontSize="8.5" fill="#fff">solo {total} participantes</text>
      <text x={r} y={r + 27} textAnchor="middle" fontFamily={FONT} fontWeight="600" fontSize="8.5" fill="#fff">en sorteo</text>
    </svg>
  );
}
Seal.propTypes = { price: PropTypes.number, total: PropTypes.number, size: PropTypes.number };

const RaffleTicket = forwardRef(({ raffle, number, buyer = {}, scale = 1 }, ref) => {
  const r = raffle || {};
  const date = fmtDateParts(r.drawDate);
  const brand = (r.ticketBrand || '').toUpperCase();
  const headline = (r.ticketHeadline || 'BOLETO SORTEO').toUpperCase();
  const venue = (r.venue || '').toUpperCase();
  const footer = r.ticketFooter || 'B&B Target Center Inc.';
  const phone = r.contactPhone || '787-755-9060';
  const stubField = (label, value) => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, borderBottom: `1px solid ${NAVY}`, paddingBottom: 2, minHeight: 22 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: NAVY, letterSpacing: 0.5 }}>{label}:</span>
      <span style={{ fontSize: 11, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value || ''}</span>
    </div>
  );

  return (
    <div style={{ width: TICKET_W * scale, height: TICKET_H * scale, overflow: 'hidden' }}>
      <div ref={ref} style={{ width: TICKET_W, height: TICKET_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative', background: '#fffdf7', fontFamily: FONT, color: NAVY, boxSizing: 'border-box', border: '1px solid #e2ddd0', overflow: 'hidden' }}>
        {/* Ondas de fondo */}
        <svg width={TICKET_W} height={TICKET_H} viewBox={`0 0 ${TICKET_W} ${TICKET_H}`} style={{ position: 'absolute', inset: 0 }}>
          <path d="M240,0 C420,60 380,200 520,250 C640,290 760,250 1000,330 L1000,0 Z" fill={GRAY} opacity="0.55" />
          <path d="M240,380 C360,300 300,180 430,140 C560,100 700,180 1000,120 L1000,380 Z" fill={GOLD} opacity="0.75" />
          <path d="M0,300 C120,330 180,260 240,380 L0,380 Z" fill={GOLD} opacity="0.75" />
        </svg>

        {/* Talón (izquierda) */}
        <div style={{ position: 'absolute', left: 0, top: 0, width: 230, height: TICKET_H, borderRight: `2px dashed ${NAVY}`, padding: '16px 14px 14px 16px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <img src="/assets/bbtc-logo.png" alt="B&B" style={{ width: 34, height: 34, objectFit: 'contain' }} crossOrigin="anonymous" />
            <span style={{ fontSize: 26, fontWeight: 800, color: RED, letterSpacing: -0.5 }}>#{number}</span>
          </div>
          {stubField('NOMBRE', buyer.name)}
          {stubField('TEL.', buyer.phone)}
          {stubField('EMAIL', buyer.email)}
          {stubField('DIRECCIÓN', buyer.address)}
          <div style={{ marginTop: 'auto', fontSize: 8.5, color: '#555', lineHeight: 1.3 }}>{footer}</div>
        </div>

        {/* Cuerpo */}
        <div style={{ position: 'absolute', left: 250, top: 18, right: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', whiteSpace: 'nowrap' }}>
          {brand && <div style={{ fontSize: 30, fontWeight: 800, color: BLUE, letterSpacing: 3, lineHeight: 1 }}>{brand}</div>}
          <div style={{ fontSize: 34, fontWeight: 800, color: NAVY, letterSpacing: 2, lineHeight: 1.1, marginTop: brand ? 2 : 8 }}>{headline}</div>
          {venue && <div style={{ marginTop: 8, border: `1.5px solid ${BLUE}`, borderRadius: 999, padding: '4px 22px', fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: NAVY, background: 'rgba(255,255,255,0.7)' }}>EN {venue}</div>}
        </div>

        {/* Logo + fecha */}
        <div style={{ position: 'absolute', left: 250, top: 150, display: 'flex', gap: 12, alignItems: 'center' }}>
          <img src="/assets/bbtc-logo.png" alt="B&B Target Center" style={{ width: 70, height: 70, objectFit: 'contain' }} crossOrigin="anonymous" />
          {date && (
            <div style={{ lineHeight: 1.1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Día Sorteo:</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{date.line1}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{date.line2}</div>
            </div>
          )}
        </div>

        {/* Foto del premio */}
        {r.imageUrl && (
          <div style={{ position: 'absolute', left: 420, top: 140, width: 300, height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={r.imageUrl} alt="" crossOrigin="anonymous" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.25))' }} />
          </div>
        )}

        {/* Premio / valor */}
        <div style={{ position: 'absolute', left: 250, top: 262, width: 440, fontSize: 12.5, lineHeight: 1.45 }}>
          <div style={{ display: 'flex', gap: 8 }}><span style={{ fontWeight: 800, minWidth: 110 }}>Premio incluye:</span><span style={{ color: '#222' }}>{r.prize}</span></div>
          {r.prizeValue ? <div style={{ display: 'flex', gap: 8 }}><span style={{ fontWeight: 800, minWidth: 110 }}>Valor:</span><span style={{ color: '#222' }}>{money(r.prizeValue)}</span></div> : null}
        </div>

        {/* Boletos en / información */}
        <div style={{ position: 'absolute', left: 720, top: 262, width: 265, fontSize: 11.5, lineHeight: 1.45 }}>
          <div><span style={{ fontWeight: 800 }}>Boletos en:</span> <span style={{ color: '#222' }}>B&B Target Center Inc.</span></div>
          <div><span style={{ fontWeight: 800 }}>Información:</span> <span style={{ color: '#222' }}>{phone}</span></div>
          <div style={{ fontWeight: 700, marginTop: 2 }}>¡No tienes que estar presente para ganar!</div>
        </div>

        {/* Sello + número (derecha) */}
        <div style={{ position: 'absolute', right: 18, top: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 30, fontWeight: 800, color: RED }}>#{number}</span>
          <Seal price={r.ticketPrice} total={r.totalTickets} />
        </div>

        {/* Pie: comprador */}
        {buyer.name && (
          <div style={{ position: 'absolute', left: 250, bottom: 10, fontSize: 10, color: '#555' }}>Boleto virtual · {buyer.name}{buyer.phone ? ` · ${buyer.phone}` : ''}</div>
        )}
      </div>
    </div>
  );
});

RaffleTicket.propTypes = {
  raffle: PropTypes.object,
  number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  buyer: PropTypes.object,
  scale: PropTypes.number,
};

export default RaffleTicket;

// La fuente Inter viene de Google Fonts (otro origen): html-to-image no puede
// leer esas reglas CSS, así que bajamos el CSS y los woff2 latinos una vez y
// los incrustamos como data URLs. Si algo falla, se exporta con la fuente del
// sistema (skipFonts).
let fontCssPromise = null;
async function interEmbedCss() {
  if (!fontCssPromise) {
    fontCssPromise = (async () => {
      const css = await fetch('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap').then((r) => r.text());
      const blocks = css.split('@font-face').slice(1).map((b) => `@font-face${b}`).filter((b) => /\/\* latin \*\//.test(b));
      const out = await Promise.all(blocks.map(async (b) => {
        const m = b.match(/url\((https:[^)]+)\)/);
        if (!m) return b;
        const buf = await fetch(m[1]).then((r) => r.arrayBuffer());
        let bin = '';
        new Uint8Array(buf).forEach((x) => { bin += String.fromCharCode(x); });
        return b.replace(m[1], `data:font/woff2;base64,${btoa(bin)}`);
      }));
      return out.join('\n');
    })().catch((e) => { console.warn('No se pudo incrustar la fuente', e); fontCssPromise = null; return null; });
  }
  return fontCssPromise;
}

// Descarga el boleto como PNG (2x para que se vea nítido en el teléfono).
export async function downloadTicketPng(node, filename) {
  const fontEmbedCSS = await interEmbedCss();
  const base = { pixelRatio: 2, cacheBust: true, backgroundColor: '#fffdf7', width: TICKET_W, height: TICKET_H, style: { transform: 'none' } };
  const dataUrl = await toPng(node, fontEmbedCSS ? { ...base, fontEmbedCSS } : { ...base, skipFonts: true });
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}
